import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

import PublishedFormPage from "../pages/PublishedFormPage";

//  Context imports
import { useFormContext } from "../contexts/FormContext";
import { FormThemeProvider } from "../contexts/FormThemeContext";
import { FieldMetadataProvider } from "../contexts/FieldMetadataContext";
import { FormLayoutProvider } from "../contexts/FormLayoutContext";
import { FieldEditProvider } from "../contexts/FieldEditContext";
import { MultiAnswerFieldProvider } from "../contexts/MultiAnswerFieldContext";
import { DropdownDependenciesProvider } from "../contexts/DropdownDependenciesContext";
import { FormSubmissionSettingsProvider } from "../contexts/FormSubmissionSettingsContext";
import { FormActionsProvider } from "../contexts/FormActionsContext";
import { FormSubmissionsProvider } from "../contexts/FormSubmissionsContext";
import { FormSaveProvider } from "../contexts/FormSaveContext";
import { FormPublishProvider } from "../contexts/FormPublishContext";

const InnerPublicFormPreview = () => {
//  console.log("[InnerPublicFormPreview] 🟢 Starting component render");

  const { formId } = useParams();
  const [searchParams] = useSearchParams();
  const previewToken = searchParams.get("token");

  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");

  const { setFormId, setFormIdReady } = useFormContext();

useEffect(() => {
  const hydratePreview = async () => {
    try {
//      console.log("[PublicFormPreviewWrapper] 🔍 formId from URL:", formId);
//      console.log("[PublicFormPreviewWrapper] 🔍 previewToken from URL:", previewToken);

const allKeys = {
  layoutConfig: `form_${formId}_layoutConfig`,
  selectedTheme: `form_${formId}_selectedTheme`,
  colors: `form_${formId}_colors`,
  fonts: `form_${formId}_fonts`,
  submitButtonStyle: `form_${formId}_submitButtonStyle`,
  fieldVariant: `form_${formId}_fieldVariant`,
  fieldsMetadata: `form_${formId}_fieldsMetadata`,
  dropdownOptions: `form_${formId}_dropdownOptions`,
  dropdownDependencies: `form_${formId}_dropdownDependencies`,
  submissionSettings: `submissionSettings_${formId}`,
  formDetails: `form_${formId}_formDetails`,
};

const requiredKeys = [allKeys.layoutConfig, allKeys.fieldsMetadata];
const optionalKeys = [
  allKeys.selectedTheme,
  allKeys.colors,
  allKeys.fonts,
  allKeys.submitButtonStyle,
  allKeys.fieldVariant,
  allKeys.dropdownOptions,
  allKeys.dropdownDependencies,
  allKeys.submissionSettings,
  allKeys.formDetails,
];


//      console.log("[PublicFormPreviewWrapper] 🧠 Required sessionStorage keys:", JSON.stringify(requiredKeys));
//      console.log("[PublicFormPreviewWrapper] 🧠 Optional sessionStorage keys:", JSON.stringify(optionalKeys));

      for (const key of [...requiredKeys, ...optionalKeys]) {
        const raw = sessionStorage.getItem(key);
//        console.log(`📦 Key check: '${key}' raw value preview:`, raw?.slice(0, 150));
      }

      try {
        const raw = sessionStorage.getItem(allKeys.fieldsMetadata);
//        console.log("🔍 Raw fieldsMetadata string from sessionStorage:", raw?.slice(0, 300));
        const parsed = JSON.parse(raw);
        const value = parsed?.value;
        if (typeof value === "object") {
          const fieldKeys = Object.keys(value);
//          console.log("✅ Parsed fieldsMetadata.value keys:", JSON.stringify(fieldKeys));
//          console.log(`📦 fieldsMetadata contains ${fieldKeys.length} fields.`);
        } else {
          console.warn("⚠️ Parsed fieldsMetadata.value is not an object.");
        }
      } catch (err) {
        console.error("❌ Failed to parse fieldsMetadata from sessionStorage:", err);
      }

      const missingRequired = requiredKeys.filter((key) => {
        try {
          const raw = sessionStorage.getItem(key);
          if (!raw) return true;
          const parsed = JSON.parse(raw);
          const actualValue = parsed?.value;
          const isEmpty =
            actualValue == null ||
            (typeof actualValue === "object" && Object.keys(actualValue).length === 0);
          if (isEmpty) {
            console.warn(`[PublicFormPreviewWrapper] ⚠️ Key "${key}" exists but .value is empty.`);
          }
          return isEmpty;
        } catch (err) {
          console.error(`[PublicFormPreviewWrapper] ❌ Failed to parse sessionStorage key "${key}":`, err);
          return true;
        }
      });

//      console.log("[PublicFormPreviewWrapper] ✅ Present sessionStorage keys:", JSON.stringify(requiredKeys));
//      console.log("[PublicFormPreviewWrapper] ❌ Missing required keys:", JSON.stringify(missingRequired));

      const isPublicPreview = (() => {
        try {
          const raw = sessionStorage.getItem("isPublicPreview");
//          console.log("[PublicFormPreviewWrapper] 📦 Raw isPublicPreview:", raw);
          const parsed = JSON.parse(raw);
          const result = parsed?.value === true;
//          console.log("[PublicFormPreviewWrapper] 🧪 Parsed isPublicPreview.value:", parsed?.value);
          return result;
        } catch (err) {
          console.warn("[PublicFormPreviewWrapper] ⚠️ Failed to parse isPublicPreview flag:", err);
          return false;
        }
      })();

      if (isPublicPreview && missingRequired.length === 0) {
//        console.log("[PublicFormPreviewWrapper] 🧪 isPublicPreview mode — skipping Firestore validation.");

        try {
//          console.log("[PublicFormPreviewWrapper] 🧩 Setting formId...");
          setFormId(formId);
//          console.log("[PublicFormPreviewWrapper] ✅ formId set successfully");

          // ✅ FIX: Defer setFormIdReady and setLoaded to avoid JSHandle errors in Puppeteer
          setTimeout(() => {
            try {
//              setFormIdReady(true);
 const skipSetReady = isPublicPreview;      // headless screenshot flow
 if (!skipSetReady && typeof setFormIdReady === "function") {
   try { setFormIdReady(true); } catch { /* ignore */ }
 }

//              console.log("[PublicFormPreviewWrapper] ✅ formIdReady set successfully (via timeout)");
              setLoaded(true);
//              console.log("[PublicFormPreviewWrapper] ✅ Loaded state set successfully (via timeout)");
            } catch (ctxErr) {
              console.error("[PublicFormPreviewWrapper] ❌ Error during delayed formIdReady set:", ctxErr);
              setError("Error initializing preview context.");
            }
          }, 10);

        } catch (ctxErr) {
          console.error("[PublicFormPreviewWrapper] ❌ Error while setting context state during preview hydration:", ctxErr);
          setError("Error initializing preview context.");
        }

        return;
      }

      if (missingRequired.length === 0) {
//        console.log("[PublicFormPreviewWrapper] ✅ All critical session data present — validating previewToken via Firestore...");

        const formRef = doc(db, "forms", formId);
        const snap = await getDoc(formRef);

        if (!snap.exists()) {
          console.error("[PublicFormPreviewWrapper] ❌ Form not found in Firestore.");
          setError("Form not found.");
          return;
        }

        const data = snap.data();
        if (!data.previewToken) {
          console.error("[PublicFormPreviewWrapper] ❌ Form exists but no previewToken found in Firestore.");
          setError("Missing preview token.");
          return;
        }

        if (data.previewToken !== previewToken) {
          console.error("[PublicFormPreviewWrapper] ❌ Preview token mismatch.");
//          console.log("🔐 Expected:", data.previewToken);
//          console.log("🔐 Received:", previewToken);
          setError("Invalid preview token.");
          return;
        }

//        console.log("[PublicFormPreviewWrapper] ✅ Token validated. Proceeding to render...");

        try {
//          console.log("[PublicFormPreviewWrapper] 🧩 Setting formId...");
          setFormId(formId);
//          console.log("[PublicFormPreviewWrapper] ✅ formId set successfully");

//          console.log("[PublicFormPreviewWrapper] 🧩 Deferring formIdReady using setTimeout...");
          setTimeout(() => {
            try {
//              setFormIdReady(true);
 const skipSetReady = isPublicPreview;      // headless screenshot flow
 if (!skipSetReady && typeof setFormIdReady === "function") {
   try { setFormIdReady(true); } catch { /* ignore */ }
 }

//              console.log("[PublicFormPreviewWrapper] ✅ formIdReady set successfully (via timeout)");
              setLoaded(true);
//              console.log("[PublicFormPreviewWrapper] ✅ Loaded state set successfully (via timeout)");
            } catch (ctxErr) {
              console.error("[PublicFormPreviewWrapper] ❌ Error during delayed formIdReady set:", ctxErr);
              setError("Error initializing preview context.");
            }
          }, 0);
        } catch (ctxErr) {
          console.error("[PublicFormPreviewWrapper] ❌ Error while setting context state after token validation:", ctxErr);
          setError("Error initializing preview context.");
        }
      } else {
        console.error("[PublicFormPreviewWrapper] ❌ Missing required sessionStorage data. Cannot render.");
        setError("Unable to render preview — required form data not found.");
      }
    } catch (err) {
      console.error("[PublicFormPreviewWrapper] ❌ Unexpected error in hydration flow:", err);
      setError("Something went wrong loading the form preview.");
    }
  };

  hydratePreview();
}, [formId, previewToken, setFormId, setFormIdReady]);

 if (error) {
   return (
     <div data-testid="form-preview-root">
       {error}
     </div>
   );
 }


  if (!loaded) {
//    console.log("[PublicFormPreviewWrapper] ⏳ Waiting for sessionStorage + Firestore validation...");
    return null;
  }

//  console.log("[InnerPublicFormPreview] ✅ Successfully reached preview root render");
//  console.log("[PublicFormPreviewWrapper] 🚀 Rendering PublishedFormPage...");

  return (
    <FormThemeProvider>
      <FieldMetadataProvider>
        <FormLayoutProvider>
          <FieldEditProvider>
            <MultiAnswerFieldProvider>
              <DropdownDependenciesProvider>
                <FormSubmissionSettingsProvider>
                  <FormActionsProvider>
                    <FormSubmissionsProvider>
                      <FormSaveProvider>
                        <FormPublishProvider>
                          <PublishedFormPage />
                        </FormPublishProvider>
                      </FormSaveProvider>
                    </FormSubmissionsProvider>
                  </FormActionsProvider>
                </FormSubmissionSettingsProvider>
              </DropdownDependenciesProvider>
            </MultiAnswerFieldProvider>
          </FieldEditProvider>
        </FormLayoutProvider>
      </FieldMetadataProvider>
    </FormThemeProvider>
  );
};

export default InnerPublicFormPreview;
